/**
 * Facets Drawer - Sort & Filter Drawer Controller
 */
class FacetsDrawer {
  constructor() {
    this.drawer = document.getElementById("facetsDrawer");
    this.triggers = document.querySelectorAll("[data-drawer-trigger]");
    this.closeButtons = document.querySelectorAll("[data-drawer-close]");
    this.filterToggles = document.querySelectorAll("[data-filter-toggle]");
    this.applyButton = document.querySelector("[data-apply-filters]");
    this.form = document.getElementById("FacetsDrawerApplyForm");
    this.isOpen = false;

    this.init();
  }

  init() {
    if (!this.drawer) {
      console.warn("Facets drawer not found");
      return;
    }

    this.setupEventListeners();
    this.setupKeyboardControls();
    this.setupFilterToggles();
    this.setupApplyButton();
  }

  setupEventListeners() {
    // Open drawer on trigger click
    this.triggers.forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close drawer on close button clicks
    this.closeButtons.forEach((button) => {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    });

    // Close drawer on backdrop click
    const backdrop = this.drawer.querySelector(".facets-drawer__backdrop");
    if (backdrop) {
      backdrop.addEventListener("click", (e) => {
        e.preventDefault();
        this.close();
      });
    }

    // Prevent closing when clicking inside the panel
    const panel = this.drawer.querySelector(".facets-drawer__panel");
    if (panel) {
      panel.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }
  }

  setupKeyboardControls() {
    // Close drawer on Escape key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  }

  setupFilterToggles() {
    // Re-query filter toggles to get current DOM state
    this.filterToggles = document.querySelectorAll("[data-filter-toggle]");

    // Remove existing listeners by cloning nodes (prevents duplicate listeners)
    this.filterToggles.forEach((toggle) => {
      const newToggle = toggle.cloneNode(true);
      toggle.parentNode.replaceChild(newToggle, toggle);
    });

    // Re-query after cloning
    this.filterToggles = document.querySelectorAll("[data-filter-toggle]");

    // Add event listeners
    this.filterToggles.forEach((toggle) => {
      toggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleFilter(toggle);
      });
    });
  }

  setupApplyButton() {
    if (!this.applyButton) return;

    this.applyButton.addEventListener("click", (e) => {
      e.preventDefault();
      this.applyFilters();
    });
  }

  applyFilters() {
    if (!this.form) return;

    // Collect existing params and preserve price params unless user changed them
    const params = [];
    const currentParams = new URLSearchParams(window.location.search);
    for (const [key, value] of currentParams.entries()) {
      if (key === "sort_by") continue;

      // Preserve existing price params if inputs are untouched
      if (key.startsWith("filter.v.price.")) {
        const priceInput = this.form.querySelector(`input[name="${key}"]`);
        const inputVal = priceInput ? (priceInput.value || "").trim() : "";
        const initialVal = priceInput
          ? (priceInput.dataset.initialValue || "").trim()
          : "";
        // Changed if: user typed a new value (inputVal !== "" and != initial) OR cleared a previously set value (inputVal === "" and initialVal !== "")
        const changed = priceInput
          ? inputVal !== ""
            ? inputVal !== initialVal
            : initialVal !== ""
          : false;

        // If not changed, keep existing param from URL
        if (!changed) {
          params.push(`${key}=${encodeURIComponent(value)}`);
        }
        // If changed, do not keep this old value here (it will be updated/removed below)
        continue;
      }

      // Preserve all non-filter params
      if (!key.startsWith("filter.")) {
        params.push(`${key}=${encodeURIComponent(value)}`);
      }
    }

    // Add sort_by if present
    const sortInput = this.form.querySelector('input[name="sort_by"]:checked');
    if (sortInput && sortInput.value && sortInput.value !== "manual") {
      params.push(`sort_by=${encodeURIComponent(sortInput.value)}`);
    }

    // Collect filter values (support multiple values per key)
    const filterGroups = {};

    // 1) Checked checkboxes replace their group entirely
    const checkedBoxes = this.form.querySelectorAll(
      'input[type="checkbox"]:checked',
    );
    checkedBoxes.forEach((input) => {
      const name = input.name;
      const value = input.value;

      if (!name || !value) return;
      if (!name.startsWith("filter.")) return;

      if (!filterGroups[name]) {
        filterGroups[name] = [];
      }
      filterGroups[name].push(value);
    });

    // 2) Price inputs: include ONLY if changed; otherwise preserve existing (already handled above)
    const priceInputs = this.form.querySelectorAll(
      'input[type="number"][name^="filter.v.price."]',
    );
    priceInputs.forEach((input) => {
      const name = input.name;
      const value = (input.value || "").trim();
      const initialVal = (input.dataset.initialValue || "").trim();

      // Determine if user changed this field:
      // - value provided and different from initial -> changed
      // - value empty but initial had a value -> user cleared -> changed (removal)
      const changed = value !== "" ? value !== initialVal : initialVal !== "";

      if (!changed) return; // unchanged: skip (we already preserved existing param)

      // If cleared (value === ""), do not add — this removes the param
      if (value === "") return;

      // Set/replace with new value
      filterGroups[name] = [value];
    });

    // Append grouped filters using repeated keys (Shopify expects repeated params)
    Object.entries(filterGroups).forEach(([key, values]) => {
      values.forEach((v) => {
        params.push(`${key}=${encodeURIComponent(v)}`);
      });
    });

    // Auto-apply "in stock" whenever any filter is active. Shopify intersects
    // variant-level filters (e.g. size) with availability on the SAME variant,
    // so this yields true per-size in-stock results. The availability filter UI
    // is hidden; this is applied transparently. No filters → don't force it.
    const hasActiveFilter = params.some((p) => p.startsWith("filter."));
    const hasAvailability = params.some((p) =>
      p.startsWith("filter.v.availability="),
    );
    if (hasActiveFilter && !hasAvailability) {
      params.push("filter.v.availability=1");
    }

    // Build and navigate to new URL
    const queryString = params.length ? `?${params.join("&")}` : "";
    const newUrl = `${window.location.pathname}${queryString}`;
    window.location.href = newUrl;
  }

  toggleFilter(toggle) {
    const isExpanded = toggle.getAttribute("aria-expanded") === "true";
    const content = toggle.nextElementSibling;

    if (!content) return;

    if (isExpanded) {
      // Collapse
      toggle.setAttribute("aria-expanded", "false");
      toggle.classList.remove("is-expanded");
      content.style.maxHeight = null;
    } else {
      // Expand
      toggle.setAttribute("aria-expanded", "true");
      toggle.classList.add("is-expanded");
      content.style.maxHeight = content.scrollHeight + "px";
    }
  }

  open() {
    if (this.isOpen) return;

    this.drawer.classList.add("is-open");
    this.isOpen = true;

    // Reinitialize filter toggles to ensure they work
    this.setupFilterToggles();

    // Prevent body scroll when drawer is open
    document.body.classList.add("facets-drawer-open");

    // Focus trap - focus the first focusable element in the drawer
    setTimeout(() => {
      const firstFocusable = this.drawer.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }, 300); // Wait for animation to complete

    // Dispatch custom event
    this.drawer.dispatchEvent(
      new CustomEvent("drawer:open", {
        bubbles: true,
        detail: { drawer: this },
      }),
    );
  }

  close() {
    if (!this.isOpen) return;

    this.drawer.classList.remove("is-open");
    this.isOpen = false;

    // Re-enable body scroll
    document.body.classList.remove("facets-drawer-open");

    // Return focus to the trigger button
    const activeTrigger = document.querySelector(
      "[data-drawer-trigger]:focus, [data-drawer-trigger]:hover",
    );
    if (activeTrigger) {
      setTimeout(() => {
        activeTrigger.focus();
      }, 300);
    }

    // Dispatch custom event
    this.drawer.dispatchEvent(
      new CustomEvent("drawer:close", {
        bubbles: true,
        detail: { drawer: this },
      }),
    );
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  // Public API
  isDrawerOpen() {
    return this.isOpen;
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    window.facetsDrawer = new FacetsDrawer();
  });
} else {
  window.facetsDrawer = new FacetsDrawer();
}

// Handle dynamic content loading (for AJAX/Shopify sections)
document.addEventListener("shopify:section:load", () => {
  if (window.facetsDrawer) {
    window.facetsDrawer = new FacetsDrawer();
  }
});
